/**
 * Client-side image compression for upload-bound photos.
 *
 * Modern tablet/phone cameras routinely produce 10–20 MB JPEGs which trip
 * the backend's 10 MB upload cap (see services/storage_service.py). This
 * utility downscales any oversized image to a max edge of `maxEdge` px and
 * re-encodes as JPEG at the given quality. Images already under the
 * threshold are passed through untouched so we don't waste cycles.
 *
 * Returns a Promise<File> with the same `name` and a `.jpg` extension.
 */
export async function compressImageFile(
  file,
  { maxEdge = 1600, quality = 0.8, sizeThresholdBytes = 1.5 * 1024 * 1024 } = {}
) {
  // Pass small images through unchanged
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  if (file.size <= sizeThresholdBytes) return file;

  // HEIC files can't be decoded by <img> in most browsers — let the
  // backend reject them with a clear error rather than fail silently here.
  if (/heic|heif/i.test(file.type) || /\.heic$|\.heif$/i.test(file.name || '')) {
    return file;
  }

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read image'));
    reader.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Failed to decode image'));
    el.src = dataUrl;
  });

  // Compute target size preserving aspect ratio
  let { width, height } = img;
  if (width > maxEdge || height > maxEdge) {
    if (width >= height) {
      height = Math.round((height / width) * maxEdge);
      width = maxEdge;
    } else {
      width = Math.round((width / height) * maxEdge);
      height = maxEdge;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', quality);
  });
  if (!blob) return file; // canvas/toBlob failed — fall back to original

  const newName = (file.name || 'photo').replace(/\.(jpe?g|png|webp|bmp|tiff?)$/i, '') + '.jpg';
  return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
}

/**
 * Convenience: compress an array of File objects, preserving order.
 * Failures fall back to the original file rather than throwing — the
 * backend still gets a chance to accept (or reject with a clear message).
 */
export async function compressImageFiles(files, opts) {
  const out = [];
  for (const f of files) {
    try {
      out.push(await compressImageFile(f, opts));
    } catch (_err) {
      out.push(f);
    }
  }
  return out;
}
